<template>
  <v-row
    align="center"
    justify="center"
    no-gutters
  >
    <v-icon
      v-if="canShow"
      size="24"
      right
      class="px-0"
      @click="openModal"
    >
      mdi-swap-horizontal
    </v-icon>

    <v-overlay
      :value="popup"
      :dark="false"
    >
      <v-card width="1200" color="white">
        <v-card-title class="full-width third px-0">
          <v-col cols="1">
            <v-icon icon large color="white">
              mdi-swap-horizontal
            </v-icon>
          </v-col>
          <v-col cols="10">
            <span class="white--text">Changement de session du cavalier</span>
          </v-col>
          <v-col cols="1">
            <v-icon size="28" color="white" class="justify-end" @click="closeModal">mdi-close</v-icon>
          </v-col>
        </v-card-title>
        <v-card-text v-if="!loading" class="pa-4">
          <Stepper
            class="mt-2 mb-4 theme--light"
            :steps="steps"
            step-color="third"
            :value="currentStep"
            @input="changeCurrentStep"
          />
          <TransferReview v-if="isConfirmation" :reviews="review" />
          <div v-else>
            <v-text-field
              v-model="search"
              light
              append-icon="mdi-magnify"
              label="Search"
              hide-details
              class="pb-4 pt-0 mt-0"
              color="third"
              label-color="grey"
              @click.stop
            />
            <v-data-table
              v-model="selectedItem"
              light
              :headers="headers"
              :items="items"
              :search="search"
              :hide-default-footer="true"
              :items-per-page="-1"
              height="300px"
              show-select
              single-select
              item-key="uuid"
              checkbox-color="third"
              @click:row="rowClick"
            />
          </div>
          <div v-if="errors.length && isConfirmation" class="col-12 px-3 d-flex flex-column">
            <div v-for="(error, index) in errors" :key="`error_transferTable_${index}`">
              <v-icon v-if="error.type === 'warning'" color="yellow">mdi-alert</v-icon>
              <v-icon v-else color="red"> mdi-alert-circle </v-icon>
              <span>{{ error.message }}</span>
            </div>
          </div>
        </v-card-text>
        <div v-else class="d-flex justify-center align-center full-height" style="height:300px">
          <Loader />
        </div>
        <v-card-actions class="pb-6 px-6 justify-end">
          <v-btn
            class="third--text"
            light
            rounded
            outlined
            :disabled="!confirmBtnEnabled"
            @click="confirmAction"
          >
            <v-icon small>
              mdi-plus
            </v-icon>
            {{ buttonLabel }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-overlay>
  </v-row>
</template>

<script>
import { ref, computed, watch } from '@vue/composition-api';
import { format, getUnixTime, fromUnixTime } from 'date-fns';
import { fr } from 'date-fns/locale';
import store from '@/store';

import Loader from '@/components/common/Loader.vue';
import Stepper from '../customerProduct/Stepper.vue';
import TransferReview from './transfers/TransferReview.vue';
import { sendNotification } from '@/helpers/utils';
import { getAssociateRecurringSessionsByDate, addParticipantToSession, deleteContentsFromSession } from '@/helpers/amplify';
import { modifyContractProduct, getFederatedProductDetail } from '@/helpers/request';

export default {
  components: { Loader, Stepper, TransferReview },
  props: {
    session: {
      type: Object,
      default: null
    },
    participant: {
      type: String,
      default: ''
    }
  },
  setup (props) {
    /* Component funcionality variables */
    const loading = ref(false);
    const search = ref(null);
    const popup = ref(false);
    const currentStep = ref(1);
    const selectedItem = ref([]);
    const items = ref([]);
    const participant = ref(null);
    const selectedDate = computed(() => store.getters.selectedDate);

    /* Varibles fo each step */
    const selected = ref({
      session: null,
      product: null,
      offer: null,
      period: null,
      price: null
    });

    const sessions = ref([]);
    const review = ref(null);

    /* Sessions variables */
    const oldSessions = ref([]);
    const newSessions = ref([]);

    const lastSessionDate = ref(null);

    const resetSearch = () => {
      search.value = null;
    };

    const reset = () => {
      sessions.value = [];
      selectedItem.value = [];
      selected.value = {
        session: null,
        product: null,
        offer: null,
        period: null,
        price: null
      };
      resetSearch();
      changeCurrentStep('session');
    };

    const closeModal = () => {
      popup.value = false;
      reset();
    };

    const rowClick = (item, row, event) => {
      event.stopPropagation();
      row.select(!row.isSelected);
    };

    const canShow = computed(() => {
      const participantExists = props.session.participants.find(p => p.uuid === props.participant);
      if (!participantExists) return false;
      return !!participantExists.contract_number && !participantExists.isClubParticipant;
    });

    const isRecurring = computed(() => props.session.recurring);
    const alphaProducts = computed(() => store.state.products);

    const computeVisitedSteps = stepIndex => stepIndex <= currentStep.value;

    const nextStep = async () => {
      const next = currentStep.value + 1;

      const nextView = steps.value.find(s => s.index === next);

      if (nextView && nextView.aditionalInfo) {
        await nextView.aditionalInfo();
      }

      currentStep.value = next;
    };

    /*  Sessions related functions */
    const getAssociatedSessions = async () => {
      const { period } = selected.value;

      if (!period) {
        sendNotification('error', 'Vous devez choisir une période dans les sessions récurrentes.');
        return { success: false };
      }

      loading.value = true;

      oldSessions.value = await getAssociateRecurringSessionsByDate(props.session, getUnixTime(new Date(selectedDate.value)), props.session.end_recurring_date);

      if (!oldSessions.value.length) {
        sendNotification('error', 'Aucune session n\'est associée à la session.');
        closeModal();
        return { success: false };
      }

      lastSessionDate.value = oldSessions.value.filter(s => s.participants.some(p => p.uuid === participant.value.uuid)).sort((a, b) => b.end_time - a.end_time)[0].end_time;

      oldSessions.value = oldSessions.value.map(s => ({ ...s, participants: [...(s.participants || []).filter(p => p.uuid !== participant.value.uuid)] }));

      newSessions.value = await getAssociateRecurringSessionsByDate(selected.value.session, getUnixTime(new Date(selectedDate.value)), getUnixTime(new Date(period.validityEndDate)));

      loading.value = false;

      if (!newSessions.value.length) {
        sendNotification('error', 'Aucune session n\'est associée à la session vers laquelle vous essayez de transférer le cavalier.');
        closeModal();
        return { success: false };
      }

      newSessions.value = newSessions.value.map(s => ({ ...s, participants: [...s.participants, participant.value] }));

      return { success: true };
    };

    const populateReview = () => {
      review.value = [
        {
          label: 'Old Session',
          isNewSession: false,
          session: {
            name: props.session.name,
            recurring: props.session.recurring,
            start_date: props.session.start_time,
            first_session_date: format(fromUnixTime(props.session.start_recurring_date), 'dd/MM/yyyy'),
            last_session_date: format(fromUnixTime(props.session.end_recurring_date), 'dd/MM/yyyy'),
            slotsFilled: `${props.session.participants.length - 1} / ${props.session.max_participant || '-'}`
          }
        },
        {
          label: 'New Session',
          isNewSession: true,
          session: {
            name: selected.value.session.originalName,
            recurring: selected.value.session.recurring,
            start_date: selected.value.session.start_time,
            first_session_date: format(fromUnixTime(selected.value.session.start_recurring_date), 'dd/MM/yyyy'),
            last_session_date: format(fromUnixTime(selected.value.session.end_recurring_date), 'dd/MM/yyyy'),
            slotsFilled: `${selected.value.session.participants.length + 1} / ${selected.value.session.max_participant || '-'}`
          },
          contractProperties: {
            product: selected.value.product.name,
            tariff: selected.value.offer.name,
            period: selected.value.period,
            priceCategory: selectedItem.value[0].name
          }
        }
      ];
      review.value.map(item => {
        if (item.isNewSession) {
          item.session.date = item.session.recurring ? `de ${format(new Date(selectedDate.value), 'dd/MM/yyyy')} a ${format(new Date(item.contractProperties.period.validityEndDate), 'dd/MM/yyyy')}` : format(fromUnixTime(item.session.start_date), 'dd/MM/yyyy');
          return;
        }
        item.session.date = item.session.recurring ? `de ${format(new Date(selectedDate.value), 'dd/MM/yyyy')} a ${format(fromUnixTime(lastSessionDate.value), 'dd/MM/yyyy')}` : format(fromUnixTime(item.session.start_date), 'dd/MM/yyyy');
      });
    };

    const handleTransferInSessions = async () => {
      await deleteContentsFromSession(props.session, participant.value.uuid, 'FOLLOWING', 'participants')
        .catch(err => {
          console.log('ERROR deleteContentsFromSession : ', err);
          return { success: false, message: 'Problème lors de la suppression d\'un participant' };
        });

      /* Don't transfer the horse */

      const { presence, presence_status, horse, ...participantBody } = participant.value;

      await addParticipantToSession(selected.value.session.id, participantBody, 'FOLLOWING', getUnixTime(new Date(selected.value.period.validityEndDate)))
        .catch(err => {
          console.log('ERROR addParticipantToSession : ', err);
          return { success: false, message: 'Problème pour ajouter un participant' };
        });

      return { success: true, message: 'Sessions updated successfully' };
    };

    const computeProperties = () => ({
      product: {
        uuid: selected.value.product.uuid,
        name: selected.value.product.name
      },
      offer: {
        uuid: selected.value.offer.uuid,
        name: selected.value.offer.name
      },
      period: selected.value.period,
      priceCategory: {
        uuid: selected.value.price.uuid,
        name: selected.value.price.name
      }
    });

    const confirmTransfer = async () => {
      loading.value = true;

      const response = await handleTransferInSessions();

      if (!response.success) {
        sendNotification('error', 'Erreur lors de la mise à jour des sessions.');
        loading.value = false;
        closeModal();
        return;
      }

      const contractResponse = await modifyContractProduct({ contractNumber: parseInt(participant.value.contract_number), customerUuid: participant.value.uuid, newProperties: computeProperties() });

      loading.value = false;

      if (!contractResponse.success) {
        sendNotification('error', 'Une erreur survenue au moment de la sauvegarde.');
        return;
      }

      sendNotification('success', contractResponse.message || 'La modification à bien été effectuée.');
      closeModal();
    };

    const getSelectedProductDetail = async () => {
      loading.value = true;
      const response = await getFederatedProductDetail(selectedItem.value[0].uuid);

      if (response.success) {
        selectedItem.value[0] = response.data;
      }
      loading.value = false;
    };

    const steps = computed(() => [
      {
        index: 1,
        value: 'session',
        name: 'Sélection Session',
        items: sessions.value || [],
        visitedStep: computeVisitedSteps(1),
        action: () => nextStep(),
        clear: () => selected.value.session = null
      },
      {
        index: 2,
        value: 'product',
        name: 'Sélection Produit',
        items: alphaProducts.value || [],
        visitedStep: computeVisitedSteps(2),
        action: () => nextStep(),
        clear: () => selected.value.product = null
      },
      {
        index: 3,
        value: 'offer',
        name: 'Sélection tarif',
        items: selected.value.product?.dynamo_offers || [],
        visitedStep: computeVisitedSteps(3),
        aditionalInfo: () => getSelectedProductDetail(),
        action: () => nextStep(),
        clear: () => selected.value.offer = null
      },
      {
        index: 4,
        value: 'period',
        name: 'Sélection période',
        items: [...new Map((selected.value.offer?.prices || []).map(price => [price.period.uuid, price.period])).values()],
        visitedStep: computeVisitedSteps(4),
        action: () => nextStep(),
        clear: () => selected.value.period = null
      },
      {
        index: 5,
        value: 'price',
        name: 'Catégorie tarifaire',
        items: (selected.value.offer?.prices || []).filter(p => p.period?.uuid === selected.value.period?.uuid).map(price => price.priceCategory),
        visitedStep: computeVisitedSteps(5),
        action: async () => {
          if (isRecurring.value) {
            const resp = await getAssociatedSessions();
            if (!resp.success) {
              return;
            }
          }
          populateReview();
          nextStep();
        },
        clear: () => selected.value.price = null
      },
      {
        index: 6,
        value: 'confirmation',
        name: 'Confirmation',
        visitedStep: computeVisitedSteps(6),
        action: () => confirmTransfer()
      }
    ]);

    /* computed */
    const isConfirmation = computed(() => steps.value[currentStep.value - 1].value === 'confirmation');
    const confirmBtnEnabled = computed(() => (!!selectedItem.value.length || isConfirmation.value));
    const errors = computed(() => {
      const allErrors = [];

      if (oldSessions.value.length < newSessions.value.length) allErrors.push({ type: 'warning', message: `Solde du contrat : L'ancienne session attribuée comporte ${oldSessions.value.length} sessions. La nouvelle session comporte ${newSessions.value.length} sessions.` });

      return allErrors;
    });

    /* Data Table */
    const headers = [
      { text: 'Nom', value: 'name', align: 'left', sortable: true }
    ];

    /* Component funcionality functions */
    const changeCurrentStep = value => {
      if (typeof value === 'number') {
        currentStep.value = value;
        return;
      }

      const stepIndex = steps.value.findIndex(step => step.value === value);
      if (stepIndex !== -1) {
        currentStep.value = steps.value[stepIndex].index;
      }
    };

    watch(popup, newVal => {
      if (!newVal) {
        reset();
      }
    });

    /* Modal */
    const openModal = () => {
      popup.value = true;

      sessions.value = (store.state.sessions || []).filter(session => session.recurring === isRecurring.value && (isRecurring.value ? session.recurring_uuid !== props.session.recurring_uuid : session.uuid !== props.session.uuid)).map(s => ({
        ...s,
        originalName: s.name,
        name: `Session(s) du ${format(fromUnixTime(s.start_time), 'EEEE', { locale: fr })} - ${s.name} - De ${format(fromUnixTime(s.start_time), 'HH\'h\'mm', { locale: fr })} à ${format(fromUnixTime(s.end_time), 'HH\'h\'mm', { locale: fr })}`
      }));

      participant.value = props.session.participants.find(p => p.uuid === props.participant);
      items.value = sessions.value;
    };

    const buttonLabel = computed(() => (isConfirmation.value ? 'Modifier' : 'Continuer'));

    const confirmAction = () => {
      steps.value[currentStep.value - 1].action();
    };

    /* Watcher that mantains most of the component functionality in terms of populating the datatable in each step. */
    watch(currentStep, async (newVal, oldVal) => {
      resetSearch();

      /* Backward funcionality in the stepper */
      if (newVal < oldVal) {
        for (let i = newVal; i <= steps.value.length - 1; i += 1) {
          if (i < 5) {
            steps.value[i].clear();
          }
        }
        selectedItem.value = [selected.value[steps.value[newVal - 1].value]];
      }

      /* Foward funcionality in the stepper */
      if (newVal > oldVal) {
        selected.value[steps.value[newVal - 2].value] = selectedItem.value[0];
        selectedItem.value = [];
      }

      /* Populates the DataTable properties in each step */
      items.value = steps.value[newVal - 1].items;
    });

    return { buttonLabel, changeCurrentStep, currentStep, steps, loading, search, popup, openModal, closeModal, headers, items, confirmBtnEnabled, selectedItem, confirmAction, review, errors, isConfirmation, canShow, rowClick };
  }
};
</script>
